import React from 'react';
import { cn } from '../../utils/cn';
import Icon from '../AppIcon';
import AnimatedButton from './AnimatedButton';

/**
 * ErrorStates - Comprehensive error handling components
 * 
 * Components:
 * - ErrorBoundary: React error boundary
 * - ErrorMessage: Error message display
 * - ErrorPage: Full error page
 * - ErrorToast: Error notification
 * - RetryButton: Retry action button
 */

// Error Message Component
export const ErrorMessage = ({ 
  title = 'Something went wrong',
  message = 'An unexpected error occurred. Please try again.',
  variant = 'default',
  showIcon = true,
  onRetry,
  retryText = 'Try Again',
  className = ''
}) => {
  const variantClasses = {
    default: 'border-destructive/20 bg-destructive/5 text-destructive',
    warning: 'border-warning/20 bg-warning/5 text-warning',
    info: 'border-info/20 bg-info/5 text-info',
    subtle: 'border-muted bg-muted/50 text-muted-foreground'
  };

  const iconMap = {
    default: 'AlertCircle',
    warning: 'AlertTriangle',
    info: 'Info',
    subtle: 'HelpCircle'
  };

  return (
    <div className={cn(
      'rounded-lg border p-4 space-y-3',
      variantClasses[variant],
      className
    )}>
      <div className="flex items-start space-x-3">
        {showIcon && (
          <Icon 
            name={iconMap[variant]} 
            size={20} 
            className="flex-shrink-0 mt-0.5" 
          />
        )}
        <div className="flex-1 space-y-2">
          <h3 className="font-semibold text-sm">
            {title}
          </h3>
          <p className="text-sm opacity-90">
            {message}
          </p>
        </div>
      </div>
      
      {onRetry && (
        <div className="flex justify-end">
          <AnimatedButton
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="text-xs"
          >
            {retryText}
          </AnimatedButton>
        </div>
      )}
    </div>
  );
};

// Error Page Component
export const ErrorPage = ({ 
  title = 'Oops! Something went wrong',
  message = 'We encountered an unexpected error. Our team has been notified and is working to fix it.',
  code = '500',
  showIcon = true,
  onRetry,
  onGoHome,
  className = ''
}) => {
  return (
    <div className={cn(
      'min-h-screen flex items-center justify-center p-4',
      className
    )}>
      <div className="text-center space-y-6 max-w-md">
        {showIcon && (
          <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
            <Icon name="AlertCircle" size={40} className="text-destructive" />
          </div>
        )}
        
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-text-primary">
            {title}
          </h1>
          <p className="text-muted-foreground">
            {message}
          </p>
        </div>
        
        <div className="text-6xl font-bold text-muted-foreground">
          {code}
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {onRetry && (
            <AnimatedButton
              variant="default"
              onClick={onRetry}
              icon="RefreshCw"
            >
              Try Again
            </AnimatedButton>
          )}
          {onGoHome && (
            <AnimatedButton
              variant="outline"
              onClick={onGoHome}
              icon="Home"
            >
              Go Home
            </AnimatedButton>
          )}
        </div>
      </div>
    </div>
  );
};

// Error Toast Component
export const ErrorToast = ({ 
  title = 'Error',
  message,
  variant = 'error',
  onClose,
  autoClose = true,
  duration = 5000,
  className = ''
}) => {
  const [isVisible, setIsVisible] = React.useState(true);

  React.useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onClose?.(), 300);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [autoClose, duration, onClose]);

  const variantClasses = {
    error: 'border-destructive/20 bg-destructive/5 text-destructive',
    warning: 'border-warning/20 bg-warning/5 text-warning',
    info: 'border-info/20 bg-info/5 text-info',
    success: 'border-success/20 bg-success/5 text-success'
  };

  const iconMap = {
    error: 'AlertCircle',
    warning: 'AlertTriangle',
    info: 'Info',
    success: 'CheckCircle'
  };

  if (!isVisible) return null;

  return (
    <div className={cn(
      'fixed top-4 right-4 z-50 max-w-sm w-full',
      'transform transition-all duration-300',
      isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0',
      className
    )}>
      <div className={cn(
        'rounded-lg border p-4 shadow-lg backdrop-blur-sm',
        variantClasses[variant]
      )}>
        <div className="flex items-start space-x-3">
          <Icon 
            name={iconMap[variant]} 
            size={20} 
            className="flex-shrink-0 mt-0.5" 
          />
          <div className="flex-1 space-y-1">
            <h4 className="font-semibold text-sm">
              {title}
            </h4>
            {message && (
              <p className="text-sm opacity-90">
                {message}
              </p>
            )}
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1 hover:bg-muted/50 rounded"
            >
              <Icon name="X" size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Retry Button Component
export const RetryButton = ({ 
  onRetry,
  loading = false,
  children = 'Try Again',
  variant = 'outline',
  size = 'sm',
  className = ''
}) => {
  return (
    <AnimatedButton
      variant={variant}
      size={size}
      onClick={onRetry}
      loading={loading}
      icon="RefreshCw"
      className={className}
    >
      {children}
    </AnimatedButton>
  );
};

// Error Boundary Component
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error to monitoring service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorPage
          title="Something went wrong"
          message="An unexpected error occurred. Please try refreshing the page."
          onRetry={this.handleRetry}
          onGoHome={() => window.location.href = '/'}
        />
      );
    }

    return this.props.children;
  }
}

// Network Error Component
export const NetworkError = ({ 
  onRetry,
  className = ''
}) => {
  return (
    <ErrorMessage
      title="Connection Error"
      message="Unable to connect to the server. Please check your internet connection and try again."
      variant="warning"
      onRetry={onRetry}
      retryText="Retry Connection"
      className={className}
    />
  );
};

// Validation Error Component
export const ValidationError = ({ 
  errors = [],
  className = ''
}) => {
  if (!errors.length) return null;

  return (
    <div className={cn('space-y-2', className)}>
      {errors.map((error, index) => (
        <div key={index} className="flex items-center space-x-2 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{error}</span>
        </div>
      ))}
    </div>
  );
};

// Empty State Component
export const EmptyState = ({ 
  title = 'No data found',
  message = 'There are no items to display at the moment.',
  icon = 'Inbox',
  action,
  actionText = 'Get Started',
  className = ''
}) => {
  return (
    <div className={cn(
      'text-center py-12 space-y-4',
      className
    )}>
      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
        <Icon name={icon} size={32} className="text-muted-foreground" />
      </div>
      
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-text-primary">
          {title}
        </h3>
        <p className="text-muted-foreground">
          {message}
        </p>
      </div>
      
      {action && (
        <AnimatedButton
          variant="default"
          onClick={action}
          className="mt-4"
        >
          {actionText}
        </AnimatedButton>
      )}
    </div>
  );
};

// Error Summary Component
export const ErrorSummary = ({ 
  errors = [],
  className = ''
}) => {
  if (!errors.length) return null;

  return (
    <div className={cn(
      'rounded-lg border border-destructive/20 bg-destructive/5 p-4 space-y-3',
      className
    )}>
      <div className="flex items-center space-x-2">
        <Icon name="AlertCircle" size={20} className="text-destructive" />
        <h3 className="font-semibold text-destructive">
          Please fix the following errors:
        </h3>
      </div>
      
      <ul className="space-y-1">
        {errors.map((error, index) => (
          <li key={index} className="text-sm text-destructive">
            • {error}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Export all components
export default {
  ErrorMessage,
  ErrorPage,
  ErrorToast,
  RetryButton,
  ErrorBoundary,
  NetworkError,
  ValidationError,
  EmptyState,
  ErrorSummary
};